import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Save, FolderOpen, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export default function ProjectsPanel({ getCurrentSnapshot, applySnapshot, conditions }) {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const isLight = conditions?._mode === "day";

  // Theme-aware palette: GÜNDÜZ = white card + anthracite text + royal-blue accents.
  // GECE = original dark portal aesthetic with cyan neon accents.
  const panelBg     = isLight ? "#FFFFFF" : "#141414";
  const panelBorder = isLight ? "#E2E8F0" : "#1F1F1F";
  const panelShadow = isLight ? "0 1px 3px rgba(0,0,0,0.05)" : "none";
  const titleColor  = isLight ? "#1A202C" : "#FFFFFF";
  const mutedColor  = isLight ? "#4A5568" : "#8B8B95";
  const accentColor = isLight ? "#1E40AF" : "var(--neon-cyan)";
  const rowHover    = isLight ? "#EFF6FF" : "#1A1A1A";
  const saveBtnBg   = isLight ? "#1E40AF" : "var(--neon-cyan)";
  const saveBtnText = isLight ? "#FFFFFF" : "#0A0A0A";
  const loadBtnBg   = isLight ? "#FFFFFF" : "#0A0A0A";
  const loadBtnBorder = isLight ? "#1E40AF" : "var(--neon-cyan)";
  const loadBtnText = isLight ? "#1E40AF" : "var(--neon-cyan)";

  const reload = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch { /* */ }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      const snap = getCurrentSnapshot();
      await api.post("/projects", snap);
      toast.success(t("projects.toast.saved", "Project saved"));
      reload();
    } catch (e) {
      toast.error(t("projects.toast.saveFail", "Could not save: ") + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const load = async (id) => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      applySnapshot(data);
      toast.success(`"${data.project_name}" ${t("projects.toast.loaded", "loaded")}`);
    } catch {
      toast.error(t("projects.toast.loadFail", "Could not load project"));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success(t("projects.toast.deleted", "Deleted"));
      reload();
    } catch { toast.error(t("projects.toast.deleteFail", "Could not delete")); }
  };

  return (
    <div className="space-y-4" data-testid="projects-panel">
      <div className="border p-5"
        style={{ backgroundColor: panelBg, borderColor: panelBorder, boxShadow: panelShadow }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2"
            style={{ color: titleColor }}>
            <FolderOpen className="w-5 h-5" style={{ color: accentColor }}/>
            {t("projects.title", "My Projects")}
          </h2>
          <Button onClick={save} disabled={loading}
            className="rounded-none h-9 font-mono text-xs uppercase tracking-wider"
            style={{ backgroundColor: saveBtnBg, color: saveBtnText }}
            data-testid="save-project-btn">
            <Save className="w-4 h-4 mr-1"/> {t("projects.saveCurrent", "Save Current Project")}
          </Button>
        </div>
        <p className="font-mono text-[11px]" style={{ color: mutedColor }}>
          {t("projects.savedHint", "Save all project data (layers, climate, conditions, R/U metrics, AI analysis) with one click and reload later.")}
        </p>
      </div>

      <div className="border" style={{ backgroundColor: panelBg, borderColor: panelBorder }}>
        {projects.length === 0 && (
          <div className="p-8 text-center font-mono text-sm" style={{ color: mutedColor }}>
            {t("projects.empty", "No saved projects yet. Click \"Save Current Project\" above.")}
          </div>
        )}
        {projects.map((p, i) => (
          <div key={p.id}
            className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-t transition-colors"
            style={{ borderColor: panelBorder }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = rowHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
            data-testid={`project-${i}`}>
            <div className="col-span-1 font-mono text-xs" style={{ color: mutedColor }}>#{String(i + 1).padStart(2, "0")}</div>
            <div className="col-span-4">
              <div className="font-semibold text-sm" style={{ color: titleColor }}>{p.project_name}</div>
              <div className="text-[10px]" style={{ color: mutedColor }}>
                {p.customer || "—"} · {p.engineer || "—"}
              </div>
            </div>
            <div className="col-span-3 font-mono text-xs" style={{ color: titleColor }}>
              <div>U = <span style={{ color: accentColor }}>{p.metrics?.u_value?.toFixed(3) ?? "—"}</span> W/m²K</div>
              <div style={{ color: mutedColor }}>R = {p.metrics?.total_r?.toFixed(3) ?? "—"}</div>
            </div>
            <div className="col-span-2 font-mono text-xs" style={{ color: titleColor }}>
              {p.climate?.city || "—"}
              <div style={{ color: mutedColor }}>{p.climate?.climate_zone_label || p.climate_zone}</div>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1">
              <Button onClick={() => load(p.id)}
                className="rounded-none h-8 px-2 font-mono text-[10px] uppercase border"
                style={{ backgroundColor: loadBtnBg, color: loadBtnText, borderColor: loadBtnBorder }}>
                <FileText className="w-3.5 h-3.5 mr-1"/> {t("projects.load", "Load")}
              </Button>
              <button onClick={() => del(p.id)}
                className="p-1.5 transition-colors"
                style={{ backgroundColor: "transparent" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,59,48,0.15)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                title={t("projects.delete", "Delete")}
                data-testid={`del-project-${i}`}>
                <Trash2 className="w-4 h-4" style={{ color: "#FF3B30" }}/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
